import { useState, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";

type MicState = "idle" | "granted" | "denied";

interface Props {
    onComplete: () => void;
    onSkip: () => void;
}

export default function MicPermissionStep({ onComplete, onSkip }: Props) {
    const [micState, setMicState] = useState<MicState>("idle");

    const requestMic = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Stop tracks immediately — we just needed permission
            stream.getTracks().forEach((t) => t.stop());
            setMicState("granted");
        } catch (error) {
            if (error instanceof DOMException && error.name === "NotAllowedError") {
                // User denied permission — expected, don't report to Sentry
            } else {
                Sentry.captureException(error);
            }
            setMicState("denied");
        }
    }, []);

    // Auto-proceed after mic granted
    useEffect(() => {
        if (micState === "granted") {
            const timer = setTimeout(onComplete, 1200);
            return () => clearTimeout(timer);
        }
    }, [micState, onComplete]);

    // Enter to trigger allow mic
    useEffect(() => {
        if (micState !== "idle") return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                e.preventDefault();
                requestMic();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [micState, requestMic]);

    if (micState === "granted") {
        return (
            <div className="flex w-full max-w-[520px] flex-col items-center gap-8">
                {/* Green checkmark */}
                <div className="flex h-28 w-28 items-center justify-center rounded-full bg-green-50">
                    <svg
                        className="h-12 w-12 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h1 className="fraunces-soft text-4xl font-semibold leading-tight text-shadow-700">You're all set!</h1>
                <p className="text-lg text-shadow-400">Microphone is ready. Let's go!</p>
                <button
                    onClick={onComplete}
                    className="w-full rounded-xl bg-max-900 py-3.5 text-lg font-semibold text-white hover:bg-max-850 focused"
                >
                    Continue to HappyDoc
                </button>
            </div>
        );
    }

    if (micState === "denied") {
        return (
            <div className="flex w-full max-w-[520px] flex-col items-center gap-8">
                <h1 className="fraunces-soft text-4xl font-semibold leading-tight text-shadow-700">
                    Microphone blocked
                </h1>
                <div className="w-full rounded-xl border border-shadow-50 bg-shadow-25 px-8 py-6 text-left">
                    <p className="text-lg font-medium text-shadow-700">To enable your microphone:</p>
                    <ol className="mt-3 list-inside list-decimal space-y-2 text-base text-shadow-400">
                        <li>Click the lock icon in your browser's address bar</li>
                        <li>
                            Find <strong>Microphone</strong> in the permissions list
                        </li>
                        <li>
                            Set it to <strong>Allow</strong>
                        </li>
                        <li>Refresh this page</li>
                    </ol>
                </div>
                <button
                    onClick={onComplete}
                    className="w-full rounded-xl bg-max-900 py-3.5 text-lg font-semibold text-white hover:bg-max-850 focused"
                >
                    Continue to HappyDoc
                </button>
                <p className="text-sm text-shadow-400">You can explore the app without a microphone</p>
            </div>
        );
    }

    // idle state
    return (
        <div className="flex w-full max-w-[520px] flex-col items-center gap-8">
            <h1 className="fraunces-soft text-4xl font-semibold leading-tight text-shadow-700">
                Allow microphone access
            </h1>
            <p className="text-lg text-shadow-400">Recording happens on your device. Audio is encrypted.</p>

            <button
                onClick={requestMic}
                className="w-full rounded-xl bg-max-900 py-3.5 text-lg font-semibold text-white hover:bg-max-850 focused"
            >
                Allow Microphone
            </button>

            <button onClick={onSkip} className="text-lg text-shadow-400 hover:text-shadow-500 focused">
                Skip for now
            </button>

            <p className="text-sm text-shadow-400">
                Press{" "}
                <kbd className="rounded border border-shadow-100 bg-shadow-25 px-1.5 py-0.5 font-mono text-xs">
                    Enter ↵
                </kbd>{" "}
                to allow
            </p>
        </div>
    );
}
